import { computed, ref, watch } from "vue";
import { useStorage } from "vue3-storage";
import { terminalListner } from "@/utils/firebase/db";
import {
  saveDeviceSettings,
  isDisplayLayout,
  isVerticalTheme,
} from "@/utils/api/deviceSettings";
import type {
  DeviceSettings,
  DeviceWritableSettings,
  DisplayLayout,
  VerticalTheme,
} from "@/utils/api/deviceSettings";
import { isDeviceSettingsSyncEnabled } from "@/composables/deviceSettingsGate";
import { readPreview } from "@/utils/previewMode";

/**
 * 端末まわり設定の単一信頼元 (single source of truth)。
 *
 * 2 種類の設定を 1 つの composable に集約する。 正本が異なる点に注意:
 *   - 端末識別子 terminalId: **localStorage が正本** (端末ごとに設定画面で 1 度だけ設定する
 *     物理的な識別子。 MySQL/Firestore 由来ではない)。 これをキーに購読/書込先を決める。
 *   - 表示設定 (displayLayout / verticalTheme / isAudio / isLogVisible):
 *     **adbox-admin-rails(MySQL) が正本**。 Rails が Firestore `terminals/{terminalId}`
 *     document の `deviceLocalSettings` フィールドへ一方向配信する。
 *   - 設置場所カテゴリ (devicePlaceCategoryId): **MySQL が正本**。 Firestore `deviceProfile`
 *     フィールドで配信される (localStorage キャッシュは持たない)。 書込は表示設定と同じ
 *     PATCH に相乗りさせ、 端末が複数 API を叩かないようにする。
 *
 * 本 composable は module-scoped シングルトンとして次を担う:
 *   - 起動時に localStorage を seed する (Firestore 応答前のフォールバック表示)
 *   - terminalId をキーに Firestore を onSnapshot 購読し reactive state を更新する (＋
 *     localStorage へ書き戻し)。 terminalId が変われば購読を張り直す (端末初期設定に追従)
 *   - save() で表示設定を書込む (PATCH → Rails(MySQL) → Firestore → 購読で最終確定)
 *
 * consumer (Deliver / VideoPlayer / useWeatherMonitor / useTrafficMonitor /
 * useWBGTMonitor / 設定画面) は localStorage を直接読まず、 ここが公開する reactive ref を
 * 購読する。 localStorage はこの composable 内でのみ使う (表示設定はキャッシュ、
 * terminalId は正本)。
 *
 * プレビュー閲覧 (?preview=1、utils/previewMode) では localStorage を読み書きせず、
 * 指定された配信枠を購読するだけの読取専用で動く。
 *
 * 設計詳細: adbox-admin-rails docs/design/features/device_settings_sync.md
 */

// 一覧・型・実行時の検証はすべて utils/api/deviceSettings が正本。
// ここで再定義すると増やしたときに片方が漏れる (2026-08-29 に qr で踏んだ)。
export type { DisplayLayout, VerticalTheme };

// この composable 内で使う storage の最小インターフェース (vue3-storage 互換)。
interface StorageLike {
  getStorageSync: <T = unknown>(key: string) => T;
  setStorageSync: (key: string, value: unknown) => void;
  removeStorageSync: (key: string) => void;
}

// ---- module-scoped singleton state ----
const terminalId = ref<string>(""); // 正本は localStorage
const displayLayout = ref<DisplayLayout>("plane");
const verticalTheme = ref<VerticalTheme>("light");
const isAudio = ref(false);
const isLogVisible = ref(false);
// 設置場所カテゴリ。 正本は MySQL、 Firestore の deviceProfile 経由で配信される
// (deviceLocalSettings と違い端末 localStorage を正本に持たない属性)。
const devicePlaceCategoryId = ref<number | null>(null);
// 上の値を「サーバの現在値として確定できた」 terminalId。 端末が変われば自動的に未確定へ
// 戻る (別端末の値を引き継いで上書きするのを防ぐ)。 未確定のまま null を送ると設置場所を
// 消してしまうため、 書込に含めてよいかの判断に使う。
const placeCategoryKnownFor = ref<string | null>(null);
const isPlaceCategoryKnown = computed(
  () => !!terminalId.value && placeCategoryKnownFor.value === terminalId.value
);

// プレビュー閲覧 (?preview=1) は **localStorage を一切書かない**。 実端末のブラウザで
// 誤って開いても、 その端末の terminalId / 表示設定キャッシュを壊さないため。
// terminalId が指定されていればその配信枠として購読する (管理画面からのリンク)。
const preview = readPreview();

let initialized = false;
let unsubscribe: (() => void) | null = null;
let storageRef: StorageLike | null = null;

const toBoolean = (v: unknown): boolean => v === true || v === "true";

// localStorage から表示設定を seed する (Firestore 応答前の即時フォールバック)。
// 旧 isTv(boolean) → displayLayout("tv") の移行もここで吸収する。
const seedFromCache = (storage: StorageLike) => {
  const layout = storage.getStorageSync<string>("displayLayout");
  if (isDisplayLayout(layout)) {
    displayLayout.value = layout;
  } else if (storage.getStorageSync<boolean>("isTv")) {
    displayLayout.value = "tv";
  }
  const theme = storage.getStorageSync<string>("verticalTheme");
  if (isVerticalTheme(theme)) verticalTheme.value = theme;
  isAudio.value = toBoolean(storage.getStorageSync("isAudio"));
  isLogVisible.value = toBoolean(storage.getStorageSync("isLogVisible"));
};

// Firestore から配信された deviceLocalSettings を reactive state へ反映。
const applySettings = (s: Partial<DeviceSettings> | null | undefined) => {
  if (!s) return;
  if (isDisplayLayout(s.displayLayout)) {
    displayLayout.value = s.displayLayout;
  }
  if (isVerticalTheme(s.verticalTheme)) {
    verticalTheme.value = s.verticalTheme;
  }
  if (typeof s.isAudio === "boolean") isAudio.value = s.isAudio;
  if (typeof s.isLogVisible === "boolean") isLogVisible.value = s.isLogVisible;
};

// Firestore から配信された deviceProfile (設置場所/向き/サイズ) のうち、 端末が編集する
// 設置場所を reactive state へ反映する。 向き/サイズは heartbeat 実測で自動判定される
// 読取専用属性のため、 設定画面では扱わない (表示が必要になったらここへ足す)。
const applyProfile = (
  p: { placeCategoryId?: number | null } | null | undefined
) => {
  if (!p) return;
  if (p.placeCategoryId !== undefined) {
    devicePlaceCategoryId.value = p.placeCategoryId ?? null;
    placeCategoryKnownFor.value = terminalId.value;
  }
};

// 表示設定を localStorage へ書き戻す (次回起動のフォールバック源)。 terminalId は対象外 (別管理)。
const persistToCache = (storage: StorageLike) => {
  if (preview.enabled) return; // プレビューは端末の設定を書き換えない
  storage.setStorageSync("displayLayout", displayLayout.value);
  storage.setStorageSync("verticalTheme", verticalTheme.value);
  storage.setStorageSync("isAudio", isAudio.value);
  storage.setStorageSync("isLogVisible", isLogVisible.value);
  // 旧キーは掃除する (isTv は displayLayout へ移行済、 緯度経度は施設側へ移り端末では不要)
  storage.removeStorageSync("isTv");
  storage.removeStorageSync("latitude");
  storage.removeStorageSync("longitude");
};

// terminalId をキーに Firestore 購読を張り直す。 端末初期設定 (terminalId 変更) に追従する。
// ゲートで無効な端末/環境 (STG・未解放) では購読しない (= 同期 OFF、 localStorage のみ)。
const subscribe = (id: string) => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  if (!id) return;
  // プレビューは書込経路を全て塞いである読取専用なので、 STG 誤書込防止のゲートは
  // 通さずに購読してよい。 これで Netlify のデプロイプレビューでも実物どおり描ける
  // (配信内容の購読 useTerminal は元からゲート外で、 それと揃う)。
  if (!preview.enabled && !isDeviceSettingsSyncEnabled(id)) return;
  terminalListner(id, (doc: any) => {
    const data = doc.data();
    if (!data) return;
    applySettings(data.deviceLocalSettings);
    applyProfile(data.deviceProfile);
    if (storageRef) persistToCache(storageRef);
  }).then((unsub: () => void) => {
    unsubscribe = unsub;
  });
};

export function useDeviceSettings() {
  const storage = useStorage() as unknown as StorageLike;

  // 初回呼び出しでのみ seed + 購読を開始する (アプリ存続中 1 本の購読を共有)。
  if (!initialized) {
    initialized = true;
    storageRef = storage;
    // プレビューで枠を指定されていれば、 その枠として購読する (localStorage は読まない)。
    terminalId.value =
      preview.terminalId || storage.getStorageSync<string>("terminalId") || "";
    // 枠指定のプレビューでこの端末のキャッシュを seed すると、 別の枠の表示設定が
    // 混ざる。 Firestore の応答を待って実物どおりに描く。
    if (!preview.terminalId) seedFromCache(storage);
    // terminalId が決まる/変わるたびに購読を張り直す (immediate で起動時にも実行)。
    watch(terminalId, (id) => subscribe(id), { immediate: true });
  }

  // terminalId の正本は localStorage。 設定画面での端末初期設定時に呼ぶ
  // (localStorage へ保存し ref を更新 → watch が購読を張り直す)。
  const setTerminalId = (id: string) => {
    if (preview.enabled) return; // プレビューから端末の身元を変えさせない
    storage.setStorageSync("terminalId", id);
    terminalId.value = id;
  };

  // 表示設定の書込。 reactive state を即時更新 (楽観的) し、 localStorage キャッシュへ
  // 書き戻し、 Rails へ PATCH する (Rails→Firestore→購読で最終確定)。
  // ゲートで無効な端末/環境 (STG・未解放) では API 書込しない。 localStorage には反映済みの
  // ため true を返す (誤った「保存失敗」通知を出さない)。
  const save = async (
    settings: DeviceSettings | DeviceWritableSettings
  ): Promise<boolean> => {
    if (preview.enabled) return false; // プレビューは閲覧専用 (書込経路を塞ぐ)
    applySettings(settings);
    // 設置場所カテゴリは MySQL 正本 (localStorage キャッシュ対象外)。 現在値を確定できて
    // いない (配信待ち等) 端末では **キーごと落として** サーバの値を温存する。 null を送ると
    // 「未設定に戻す」と解釈され、 既存の設置場所を消してしまうため。
    const payload: DeviceSettings | DeviceWritableSettings = { ...settings };
    if ("devicePlaceCategoryId" in payload) {
      if (isPlaceCategoryKnown.value) {
        applyProfile({ placeCategoryId: payload.devicePlaceCategoryId });
      } else {
        delete (payload as DeviceWritableSettings).devicePlaceCategoryId;
      }
    }
    persistToCache(storage);
    if (!isDeviceSettingsSyncEnabled(terminalId.value)) return true;
    return saveDeviceSettings(terminalId.value, payload);
  };

  // 端末検索の結果から設置場所の現在値を確定する (選択した端末の実値をフォームへ読み込む)。
  // Firestore 配信を待たずに確定できるので、 選択直後の保存でも上書き事故が起きない。
  const setPlaceCategory = (id: number | null) => {
    devicePlaceCategoryId.value = id;
    placeCategoryKnownFor.value = terminalId.value;
  };

  return {
    terminalId,
    displayLayout,
    verticalTheme,
    isAudio,
    isLogVisible,
    devicePlaceCategoryId,
    isPlaceCategoryKnown,
    setPlaceCategory,
    setTerminalId,
    save,
  };
}
